import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CatalogScreen, CatalogTemplate, ReportSpec, ReportSummary } from 'src/components/star-grid/api/reportEngineTypes';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { useReportSpec } from 'src/components/star-grid/hooks/useReportSpec';
import { getStudioCatalog } from 'src/components/star-grid/api/reportEngineClient';
import { lineSetRowLabels } from 'src/components/star-grid/utils/lineSetRowLabels';
import { DEFAULT_ROW_SELECTOR, isSpecPreviewable } from 'src/components/star-grid/utils/reportSpec';
import { SPACER_CALC } from 'src/components/star-grid/utils/reportPivotModel';
import { studioColumnAxis, studioColumnRequests } from 'src/components/star-grid/utils/studioColumnAxis';
import { deckAxesFromShelves, specWithDeckAxes, type DeckAxes } from 'src/components/star-grid/utils/studioSpecFromShelves';
import { specDataSource } from 'src/components/star-grid/utils/studioDataSource';

/** A spec the shelves can edit before any starting point is chosen. */
export function blankReportSpec(name: string): ReportSpec {
  return {
    name,
    cube_source: '',
    row_axis: { dimension: '', hierarchy: '', selector: DEFAULT_ROW_SELECTOR },
    column_axis: [],
    filters: []
  };
}

/**
 * Projects each newly loaded spec onto the shelves. Keyed on spec identity: a shelf edit never replaces
 * the spec object, so it is never overwritten; only an import or a template choice is.
 */
function useApplyLoadedSpec(spec: ReportSpec | undefined, applySpec: (spec: ReportSpec) => void): void {
  const appliedSpecRef = useRef<ReportSpec | undefined>(undefined);
  useEffect(() => {
    if (!spec || spec === appliedSpecRef.current) return;
    appliedSpecRef.current = spec;
    applySpec(spec);
  }, [spec, applySpec]);
}

/**
 * Every deck's shelves, remembered as the deck selector moves, so saving after switching decks keeps
 * the arrangement of the decks no longer on screen. The active deck is always read live.
 */
function usePerDeckAxes(state: ReportStudioState) {
  const activeDeck = state.draft.definition.active_deck;
  const perDeckRef = useRef<Record<string, DeckAxes>>({});
  const activeAxes = useMemo(
    () => deckAxesFromShelves(state.deck, state.dataset, state.nestedRows, state.nestedLayout),
    [state.deck, state.dataset, state.nestedRows, state.nestedLayout]
  );
  useEffect(() => {
    if (!activeDeck) return;
    perDeckRef.current = { ...perDeckRef.current, [activeDeck]: activeAxes };
  }, [activeDeck, activeAxes]);
  return { activeDeck, activeAxes, perDeckRef };
}

/**
 * On a cold Create page, starts from the first template so the preview renders at once. The shelves
 * already showed a template's arrangement, but with no spec behind them the preview stayed empty until
 * a template was picked by hand. Once only: a user's own choice is never replaced.
 */
function useDefaultStartingPoint(skip: string | undefined, started: boolean, start: (template: CatalogTemplate) => void): void {
  // Refs, not effect cleanup: React re-runs effects in development, and cancelling the first run on
  // cleanup while a "tried" guard skipped the second left the Create page with no preview at all.
  const requestedRef = useRef(false);
  const startedRef = useRef(started);
  startedRef.current = started;
  const skipRef = useRef(skip);
  skipRef.current = skip;
  const startRef = useRef(start);
  startRef.current = start;
  useEffect(() => {
    if (skip || started || requestedRef.current) return;
    requestedRef.current = true;
    getStudioCatalog()
      .then((catalog) => {
        const first = catalog.templates.find((template) => template.representative_screen_id);
        // The user may have picked a template, or moved to a dataset, while the catalog loaded; theirs wins.
        if (first && !startedRef.current && !skipRef.current) startRef.current(first);
      })
      .catch(() => undefined);
  }, [skip, started]);
}

/** The studio's report spec: the shelves are the source of truth for axes, the spec for everything else. */
export function useStudioReportSpec(state: ReportStudioState, editingReportId: string | undefined, onCreated: (id: number, name?: string) => void) {
  const report = useReportSpec(editingReportId);
  const [templateLabel, setTemplateLabel] = useState('');
  // The columns live on the shelves, so they are translated per render rather than copied into the spec.
  const columnRequests = useMemo(
    () => studioColumnRequests(state.template.catalog, state.draft.definition.dataset_id, state.deck.columns),
    [state.template.catalog, state.draft.definition.dataset_id, state.deck.columns]
  );
  // Only a saved report states the user's own columns; a template or deck screen carries the deck's.
  const savedColumnsRef = useRef(Boolean(editingReportId));
  const { applySpec } = state;
  const applyLoadedSpec = useCallback((spec: ReportSpec) => applySpec(spec, { savedColumns: savedColumnsRef.current }), [applySpec]);
  useApplyLoadedSpec(report.spec, applyLoadedSpec);
  const { activeDeck, activeAxes, perDeckRef } = usePerDeckAxes(state);
  // The shelves' data source, applied before every preview and save so both run on the same source:
  // a report seeded from a P&L starting point then switched to a dataset would otherwise preview on P&L.
  const datasetId = state.dataset?.source === 'dataset' ? state.dataset.id : undefined;
  // A report reads one measure: the one its columns chose (the dataset's default otherwise).
  const measure = state.deck.columns.find((column) => column.kind === 'scenario')?.metric_id || undefined;
  const sourceChoices = useMemo(() => ({ measure }), [measure]);

  const bundledSpec = useCallback(
    () =>
      report.spec
        ? specWithDeckAxes(
            { ...report.spec, ...specDataSource(report.spec, datasetId, sourceChoices) },
            activeDeck ?? '',
            activeAxes,
            perDeckRef.current
          )
        : undefined,
    [report.spec, datasetId, sourceChoices, activeDeck, activeAxes, perDeckRef]
  );

  // The preview runs the shelves through the same `specWithDeckAxes` save persists, so they cannot drift.
  // `state.deck` is a dependency because a line-set edit changes nothing else.
  useEffect(() => {
    // Judged on the shelves, not the loaded spec, so emptied shelves clear the grid instead of keeping the template's.
    const bundled = bundledSpec();
    const hasColumns = columnRequests.some((column) => column.calc !== SPACER_CALC);
    if (!bundled || !isSpecPreviewable(bundled) || state.deck.rows.length === 0 || !hasColumns) {
      report.clear();
      return;
    }
    void report.run({ columns: columnRequests }, { ...bundled, row_labels: lineSetRowLabels(state.deck) });
  }, [report.run, report.clear, report.spec, columnRequests, activeDeck, activeAxes, state.deck, sourceChoices]);

  const startFromTemplate = useCallback(
    (chosen: CatalogTemplate) => {
      savedColumnsRef.current = false;
      setTemplateLabel(chosen.label);
      if (!chosen.representative_screen_id) {
        // Scratch empties the shelves too, or the previous template's rows would be saved with it.
        state.clearDeck();
        // The data source carries over: scratch has none of its own, and without one nothing can preview.
        report.setSpec({ ...blankReportSpec(state.draft.name), cube_source: report.spec?.cube_source ?? '', dataset: report.spec?.dataset });
        return;
      }
      void report.startFromScreen(chosen.representative_screen_id, state.draft.name || chosen.label, {
        template: chosen.id,
        template_label: chosen.label
      });
    },
    [report.startFromScreen, report.setSpec, report.spec, state.clearDeck, state.draft.name]
  );

  // A dataset's report starts blank on that dataset: the P&L templates are not its starting points.
  useEffect(() => {
    if (datasetId && !editingReportId && !report.spec) report.setSpec({ ...blankReportSpec(state.draft.name), dataset: { id: datasetId } });
  }, [datasetId, editingReportId, report.spec, report.setSpec, state.draft.name]);
  useDefaultStartingPoint(editingReportId || datasetId, Boolean(report.spec) || Boolean(templateLabel), startFromTemplate);

  const importScreenSetup = useCallback(
    (screen: CatalogScreen) => {
      savedColumnsRef.current = false;
      setTemplateLabel(screen.name);
      if (!state.draft.name) state.details({ name: screen.name });
      void report.startFromScreen(screen.id, screen.name, { template: screen.template, template_label: screen.name });
    },
    [report.startFromScreen, state.draft.name, state.details]
  );

  const importReportSetup = useCallback(
    (source: ReportSummary) => {
      savedColumnsRef.current = true;
      setTemplateLabel(source.name);
      void report.startFromReport(source.id);
    },
    [report.startFromReport]
  );

  const saveReport = useCallback(async () => {
    const bundled = bundledSpec();
    const stored = await report.save({
      name: state.draft.name,
      // `bundled` already carries the shelves' data source, so the saved report matches the preview.
      ...(bundled ? { dataset: bundled.dataset, row_axis: bundled.row_axis, filters: bundled.filters, deck_overrides: bundled.deck_overrides } : {}),
      // Renamed rows only: the rest are labelled server-side, so persisting unchanged names would go stale.
      row_labels: lineSetRowLabels(state.deck),
      ...studioColumnAxis(columnRequests)
    });
    if (stored?.id) onCreated(Number(stored.id), state.draft.name);
  }, [report.save, bundledSpec, columnRequests, state.draft.name, state.deck, onCreated]);

  return {
    report,
    // Falls back to the spec's own label, which is all a reopened report has.
    templateLabel: templateLabel || report.spec?.template_label || '',
    startFromTemplate,
    importScreenSetup,
    importReportSetup,
    saveReport
  };
}
